/**
 * Maps backend clinic API field names to the frontend form keys used
 * by the clinic setup components so server validation errors can be
 * displayed inline next to the correct inputs.
 */
const BACKEND_TO_FRONTEND_FIELD_MAP = {
  name_en: "name_en",
  name_ar: "name_ar",
  registration_number: "registrationNumber",
  type: "type",
  vat_number: "vatNumber",
  currency: "currency",
  invoice_starting_number: "invoiceStart",
  email: "email",
  mobile: "mobile",
  telephone: "telephone",
  website: "website",
  country_id: "country",
  city_id: "city",
  country: "country",
  city: "city",
  street: "street",
  building_number: "buildingNumber",
  floor_number: "floorNumber",
  latitude: "latitude",
  longitude: "longitude",
  logo: "logo",
  is_pet_store: "is_pet_store",
  services_offered: "servicesOffered",
  "services_offered.*": "servicesOffered",
  working_hours: "workingHours",
  "working_hours.*.day": "workingHours",
  "working_hours.*.from": "workingHours",
  "working_hours.*.to": "workingHours",
  "working_hours.*.enabled": "workingHours",
};

/**
 * Convert a backend validation key (including dotted array keys) to a
 * frontend form key. Returns null when there is no useful inline mapping.
 */
function mapBackendFieldToFrontend(backendKey) {
  if (!backendKey || typeof backendKey !== "string") return null;

  // Exact match first.
  if (BACKEND_TO_FRONTEND_FIELD_MAP[backendKey]) {
    return BACKEND_TO_FRONTEND_FIELD_MAP[backendKey];
  }

  // Handle dotted array keys such as services_offered.0 or working_hours.0.day.
  const baseKey = backendKey.split(".")[0];
  if (BACKEND_TO_FRONTEND_FIELD_MAP[baseKey]) {
    return BACKEND_TO_FRONTEND_FIELD_MAP[baseKey];
  }

  if (backendKey.startsWith("services_offered.")) return "servicesOffered";
  if (backendKey.startsWith("working_hours.")) return "workingHours";

  return null;
}

/**
 * Normalizes backend error messages into a string or array of strings.
 */
function normalizeMessages(raw) {
  if (Array.isArray(raw)) {
    return raw
      .filter((m) => m !== null && m !== undefined)
      .map((m) => String(m));
  }
  if (raw !== null && raw !== undefined) {
    return [String(raw)];
  }
  return [];
}

/**
 * Maps frontend form keys to the step/section index where the field lives.
 * Used to jump the user directly to the first section that has a server error.
 */
const FIELD_TO_SECTION = {
  name_en: 0,
  name_ar: 0,
  type: 0,
  email: 0,
  mobile: 0,
  telephone: 0,
  website: 0,
  logo: 0,
  registrationNumber: 1,
  vatNumber: 1,
  currency: 1,
  invoiceStart: 1,
  workingHours: 1,
  country: 2,
  city: 2,
  street: 2,
  buildingNumber: 2,
  floorNumber: 2,
  latitude: 2,
  longitude: 2,
  servicesOffered: 3,
};

/**
 * Given a map of frontend field errors, returns the index of the first
 * clinic-setup section that contains an error, or null if none match.
 *
 * @param {Record<string, string>} fieldErrors
 * @returns {number | null}
 */
export function getFirstSectionWithErrors(fieldErrors) {
  if (!fieldErrors || typeof fieldErrors !== "object") return null;
  const sections = Object.keys(fieldErrors)
    .map((key) => FIELD_TO_SECTION[key])
    .filter((section) => typeof section === "number");
  if (sections.length === 0) return null;
  return Math.min(...sections);
}

/**
 * Parses a clinic API error response and returns a user-friendly message
 * plus a map of frontend field errors for inline display.
 *
 * Supports both Laravel default validation responses:
 *   { message: "...", errors: { field: ["..."] } }
 * and the project's wrapped error responses:
 *   { status: "error", message: "...", errors: { ... } }
 *
 * @param {object} responseData - Parsed JSON response body.
 * @param {string} fallbackMessage - Message to use when the response provides none.
 * @returns {{ message: string, fieldErrors: Record<string, string> }}
 */
export function parseClinicApiError(responseData, fallbackMessage) {
  const fieldErrors = {};
  let message = fallbackMessage;

  if (!responseData || typeof responseData !== "object") {
    return { message, fieldErrors };
  }

  if (responseData.message && typeof responseData.message === "string") {
    message = responseData.message;
  } else if (responseData.error && typeof responseData.error === "string") {
    message = responseData.error;
  }

  if (responseData.errors && typeof responseData.errors === "object") {
    const allMessages = [];

    Object.entries(responseData.errors).forEach(([backendKey, rawMessages]) => {
      const messages = normalizeMessages(rawMessages);
      if (messages.length === 0) return;

      const frontendKey = mapBackendFieldToFrontend(backendKey);
      if (frontendKey) {
        const joined = messages.join(", ");
        fieldErrors[frontendKey] = fieldErrors[frontendKey]
          ? `${fieldErrors[frontendKey]}, ${joined}`
          : joined;
      }

      allMessages.push(...messages);
    });

    if (allMessages.length > 0 && message === fallbackMessage) {
      // If the server did not provide its own message, build one from the
      // individual field errors so the user still sees exactly what failed.
      message = allMessages.join("\n");
    }
  }

  return { message, fieldErrors };
}
